import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { useAuth } from '../../context/AuthContext'
import { periodParams, useDashboardPeriod } from './period'
import type { OverviewResponse, WidgetResponse } from './types'

// The layout call says which widgets this user gets; each widget then loads
// its own data. `skipErrorToast` keeps a failing widget as an inline error on
// its own card instead of a toast per widget.
const STALE_TIME = 1000 * 60

export function useDashboardOverview() {
  const { organization } = useAuth()
  return useQuery({
    queryKey: ['dashboard', 'overview', organization?.id],
    queryFn: () =>
      api.get<OverviewResponse>('/dashboard/overview', { organizationId: organization?.id }, { skipErrorToast: true }),
    enabled: !!organization?.id,
    staleTime: STALE_TIME,
  })
}

/**
 * One widget's data. Pass `{ period: true }` for a widget that follows the Company tab's date range:
 * the range is sent with the request and is part of the cache key, so changing it reloads only those widgets.
 * `params` are extra query options for one widget (the daily report's day); they are part of the cache key too.
 */
export function useWidgetData<T>(widgetId: string, options: { period?: boolean; params?: Record<string, string> } = {}) {
  const { organization } = useAuth()
  const { choice } = useDashboardPeriod()
  const range = options.period || options.params ? { ...(options.period ? periodParams(choice) : {}), ...(options.params ?? {}) } : null
  return useQuery({
    queryKey: ['dashboard', 'widget', organization?.id, widgetId, range],
    queryFn: async () => {
      const res = await api.get<WidgetResponse<T>>(
        `/dashboard/widgets/${widgetId}`,
        { organizationId: organization?.id, ...(range ?? {}) },
        { skipErrorToast: true }
      )
      return res.data
    },
    enabled: !!organization?.id,
    staleTime: STALE_TIME,
  })
}

/** After a widget changes data (log time, approve a timesheet...), reload the widgets that show it. */
export function useRefreshWidgets() {
  const queryClient = useQueryClient()
  const { organization } = useAuth()
  return (...widgetIds: string[]) =>
    Promise.all(
      widgetIds.map((id) =>
        queryClient.invalidateQueries({ queryKey: ['dashboard', 'widget', organization?.id, id] })
      )
    )
}
